import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Paper,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CircularProgress from "@mui/material/CircularProgress";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import DialogConfirm from "@jumbo/components/JumboDialog/components/DialogConfirm";
import Loader from "@jumbo/components/Loader";
import { userType } from "app/helpers/userType";
import {
  createBanner,
  deleteBanner,
  getBannerList,
  reorderBanner,
  updateBanner,
} from "app/redux/slice/bannerSlice";
import { CropDialog } from "app/components";

const MAX_FILE_SIZE = 5 * 1024 * 1024;

// The app renders banners at exactly this size, so every upload is cropped to
// it rather than trusting whatever the picker happened to hand us.
const BANNER_WIDTH = 1254;
const BANNER_HEIGHT = 340;

// The backend stores bare filenames; REACT_APP_IMAGE_BASE_URL already ends in
// "uploads/", so only the module folder needs to be added here.
const buildImageUrl = (filename) =>
  `${process.env.REACT_APP_IMAGE_BASE_URL}banner-images/${filename}`;

const Banner = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { isMA } = userType();
  const bannerData = useSelector((state) => state?.banner);

  const [selectedFiles, setSelectedFiles] = useState([]);
  // Picked files waiting to be cropped. Nothing reaches selectedFiles - and so
  // nothing can be uploaded - until it has been through the cropper.
  const [cropQueue, setCropQueue] = useState([]);
  // How many were queued in this batch, so the dialog can say "2 of 3".
  const [cropTotal, setCropTotal] = useState(0);
  const [previews, setPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Local copy so a drag reorders instantly, before the API round-trip.
  const [order, setOrder] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);

  const [permissionData] = useState(
    JSON.parse(localStorage.getItem("permission"))
  );
  const [permission, setPermission] = useState({
    createPermission: false,
    updatePermission: false,
    deletePermission: false,
  });

  useEffect(() => {
    if (isMA) {
      setPermission({
        createPermission: true,
        updatePermission: true,
        deletePermission: true,
      });
      return;
    }
    const value = {
      createPermission: false,
      updatePermission: false,
      deletePermission: false,
    };
    permissionData
      ?.filter((item) => item?.moduleName?.includes("Banner"))
      ?.forEach((item) => {
        if (item?.name === "create-banner") value.createPermission = true;
        if (item?.name === "update-banner") value.updatePermission = true;
        if (item?.name === "delete-banner") value.deletePermission = true;
      });
    setPermission(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getData = useCallback(() => {
    dispatch(getBannerList());
  }, [dispatch]);

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    setOrder(Array.isArray(bannerData?.list) ? bannerData.list : []);
  }, [bannerData?.list]);

  // Object URLs must be revoked, otherwise the picked blobs stay in memory for
  // as long as the page is open.
  useEffect(() => {
    const urls = selectedFiles.map((file) => URL.createObjectURL(file));
    setPreviews(urls);
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [selectedFiles]);

  const handleSelect = (event) => {
    const picked = Array.from(event.target.files || []);
    // Reset the input so picking the same file again still fires onChange.
    event.target.value = "";

    const accepted = [];
    picked.forEach((file) => {
      if (!file.type?.startsWith("image/")) {
        enqueueSnackbar(`${file.name} is not an image file`, {
          variant: "error",
        });
      } else if (file.size > MAX_FILE_SIZE) {
        enqueueSnackbar(`${file.name} is larger than 5MB`, {
          variant: "error",
        });
      } else {
        accepted.push(file);
      }
    });

    // Straight into the cropper - each one is added to the upload list only
    // once it has been cropped to the exact banner size.
    if (accepted.length) {
      setCropQueue((prev) => [...prev, ...accepted]);
      setCropTotal((prev) => prev + accepted.length);
    }
  };

  const advanceCropQueue = () => setCropQueue((prev) => prev.slice(1));

  const handleCropped = (croppedFile) => {
    setSelectedFiles((prev) => [...prev, croppedFile]);
    advanceCropQueue();
  };

  // Cancelling drops just that image; anything still queued is cropped next.
  const handleCropCancel = advanceCropQueue;

  // The batch is over once the queue drains, so the next pick starts at "1 of n".
  useEffect(() => {
    if (!cropQueue.length) setCropTotal(0);
  }, [cropQueue.length]);

  const handleUpload = async () => {
    if (!selectedFiles.length) return;
    setUploading(true);

    // The API takes one banner per request, so upload them in sequence - that
    // also keeps the assigned order the same as the order they were picked in.
    let failed = 0;
    for (const file of selectedFiles) {
      const formData = new FormData();
      formData.append("image", file);
      const response = await dispatch(createBanner(formData)).unwrap();
      if (response.status !== 200) {
        failed += 1;
        enqueueSnackbar(response.data?.responseMessage || "Upload failed", {
          variant: "error",
        });
      }
    }

    if (failed < selectedFiles.length) {
      enqueueSnackbar(
        `${selectedFiles.length - failed} banner(s) uploaded successfully.`,
        { variant: "success" }
      );
    }
    setSelectedFiles([]);
    setUploading(false);
    getData();
  };

  const handleToggle = async (banner) => {
    const response = await dispatch(
      updateBanner({ id: banner._id, data: { isActive: !banner.isActive } })
    ).unwrap();
    enqueueSnackbar(response.data?.responseMessage, {
      variant: response.status === 200 ? "success" : "error",
    });
    if (response.status === 200) getData();
  };

  const handleDelete = async () => {
    const id = deleteTarget;
    setDeleteTarget(null);
    const response = await dispatch(deleteBanner(id)).unwrap();
    enqueueSnackbar(response.data?.responseMessage, {
      variant: response.status === 200 ? "success" : "error",
    });
    if (response.status === 200) getData();
  };

  const persistOrder = async (nextOrder) => {
    setSavingOrder(true);
    const response = await dispatch(
      reorderBanner(nextOrder.map((b) => b._id))
    ).unwrap();

    if (response.status !== 200) {
      enqueueSnackbar(response.data?.responseMessage || "Could not save order", {
        variant: "error",
      });
      setOrder(bannerData?.list ?? []); // roll back to what the server has
    } else {
      getData();
    }
    setSavingOrder(false);
  };

  const handleDrop = (dropIndex) => {
    if (dragIndex === null || dragIndex === dropIndex) {
      setDragIndex(null);
      return;
    }
    const next = [...order];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(dropIndex, 0, moved);
    setDragIndex(null);
    setOrder(next);
    persistOrder(next);
  };

  if (bannerData?.loading && !order.length) return <Loader />;

  return (
    <Card>
      <CardContent>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1}
          sx={{ mb: 2 }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography variant="h2" mb={0}>
              App Banners
            </Typography>
            <Chip
              size="small"
              label={`${order.length} total`}
              color={order.length ? "primary" : "default"}
            />
            {savingOrder && <CircularProgress size={16} />}
          </Stack>
          <Typography variant="body2" color="text.secondary" mb={0}>
            Drag to set the order the app shows them in
          </Typography>
        </Stack>

        {permission.createPermission && (
          <>
            <input
              id="banner-upload"
              type="file"
              accept="image/*"
              multiple
              style={{ display: "none" }}
              onChange={handleSelect}
            />
            <label htmlFor="banner-upload">
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  p: 2,
                  mb: 2,
                  border: "2px dashed #ccc",
                  borderRadius: 2,
                  minHeight: 110,
                  cursor: "pointer",
                  "&:hover": { borderColor: "primary.main" },
                }}
              >
                <CloudUploadIcon color="primary" fontSize="large" />
                <Typography variant="body1" mb={0}>
                  Click to upload banners
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={0}>
                  JPG, PNG or WEBP — up to 5MB each, multiple allowed
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={0}>
                  Every image is cropped to {BANNER_WIDTH} × {BANNER_HEIGHT} px
                  before upload
                </Typography>
              </Box>
            </label>
          </>
        )}

        <CropDialog
          open={cropQueue.length > 0}
          file={cropQueue[0]}
          index={cropTotal - cropQueue.length}
          total={cropTotal}
          width={BANNER_WIDTH}
          height={BANNER_HEIGHT}
          label="Banner Image"
          onCropped={handleCropped}
          onCancel={handleCropCancel}
        />

        {selectedFiles.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {selectedFiles.length} banner(s) ready to upload
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {selectedFiles.map((file, index) => (
                <Grid item key={`${file.name}-${index}`}>
                  <Paper
                    elevation={2}
                    sx={{
                      position: "relative",
                      width: 160,
                      height: 80,
                      borderRadius: 2,
                      overflow: "hidden",
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() =>
                        setSelectedFiles((prev) =>
                          prev.filter((_, i) => i !== index)
                        )
                      }
                      sx={{
                        position: "absolute",
                        top: 2,
                        right: 2,
                        bgcolor: "rgba(255,255,255,0.85)",
                        "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                    {previews[index] && (
                      <img
                        src={previews[index]}
                        alt={file.name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
            <Stack direction="row" spacing={2}>
              <LoadingButton
                variant="contained"
                onClick={handleUpload}
                loading={uploading}
                loadingIndicator={<CircularProgress size={20} />}
              >
                Upload
              </LoadingButton>
              <LoadingButton
                variant="outlined"
                color="secondary"
                onClick={() => setSelectedFiles([])}
                disabled={uploading}
              >
                Clear
              </LoadingButton>
            </Stack>
          </Box>
        )}

        {order.length === 0 ? (
          <Typography variant="body1" sx={{ py: 3, textAlign: "center" }}>
            No banners yet.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {order.map((banner, index) => (
              <Grid item xs={12} sm={6} md={4} key={banner._id}>
                <Paper
                  elevation={3}
                  draggable={permission.updatePermission && !savingOrder}
                  onDragStart={() => setDragIndex(index)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleDrop(index)}
                  onDragEnd={() => setDragIndex(null)}
                  sx={{
                    position: "relative",
                    borderRadius: 2,
                    overflow: "hidden",
                    cursor: permission.updatePermission ? "grab" : "default",
                    opacity: dragIndex === index ? 0.4 : 1,
                    // Inactive banners are dimmed so it's obvious at a glance
                    // which ones the app is actually showing.
                    filter: banner.isActive ? "none" : "grayscale(1)",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: 6,
                      left: 6,
                      px: 0.75,
                      py: 0.25,
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.25,
                      bgcolor: "primary.main",
                      color: "common.white",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    <DragIndicatorIcon sx={{ fontSize: 14 }} />
                    {index + 1}
                  </Box>

                  {permission.deletePermission && (
                    <Tooltip title="Delete banner">
                      <IconButton
                        size="small"
                        onClick={() => setDeleteTarget(banner._id)}
                        sx={{
                          position: "absolute",
                          top: 6,
                          right: 6,
                          bgcolor: "rgba(255,255,255,0.85)",
                          "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                        }}
                      >
                        <DeleteIcon fontSize="small" color="error" />
                      </IconButton>
                    </Tooltip>
                  )}

                  <img
                    src={buildImageUrl(banner.image)}
                    alt="Banner"
                    style={{
                      width: "100%",
                      height: 140,
                      objectFit: "cover",
                      display: "block",
                      pointerEvents: "none",
                    }}
                  />

                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ px: 1, py: 0.5 }}
                  >
                    <Typography
                      variant="body2"
                      color={banner.isActive ? "success.main" : "text.disabled"}
                      mb={0}
                    >
                      {banner.isActive ? "Active" : "Inactive"}
                    </Typography>
                    <Switch
                      size="small"
                      color="primary"
                      checked={Boolean(banner.isActive)}
                      disabled={!permission.updatePermission}
                      onChange={() => handleToggle(banner)}
                    />
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}

        <DialogConfirm
          isOpen={Boolean(deleteTarget)}
          onClose={() => setDeleteTarget(null)}
          title="Are you sure you want to delete this banner?"
          onConfirm={handleDelete}
          btnLabels={{ cancel: "Cancel", confirm: "Delete" }}
        />
      </CardContent>
    </Card>
  );
};

export default Banner;
